/**
 * Action block node for the automation flow builder.
 * Displays a list of action steps, including approval steps.
 * Supports notification, webhook, and system_control action types.
 */
import { Badge } from '@/components/ui/badge';
import type { ActionBlockConfig, ActionStep } from '@/lib/dag-types';
import type { SettingsChange } from '@/lib/control-types';
import { getActionLabel } from '@/lib/automation-trigger-types';
import { Bell, Globe, Settings2, ShieldAlert } from 'lucide-react';

const RISK_TIER_COLORS: Record<string, { border: string; text: string; bg: string }> = {
  troubleshooting: { border: 'border-wt-green-primary/40', text: 'text-wt-green-primary', bg: 'bg-wt-green-primary/8' },
  optimization: { border: 'border-wt-amber-primary/40', text: 'text-wt-amber-primary', bg: 'bg-wt-amber-primary/8' },
  setup: { border: 'border-wt-red-primary/40', text: 'text-wt-red-primary', bg: 'bg-wt-red-primary/8' },
};

const ACTION_TYPE_ICON: Record<string, typeof Bell> = {
  notification: Bell,
  webhook: Globe,
  system_control: Settings2,
};

const ACTION_TYPE_LABEL: Record<string, string> = {
  notification: 'Notification',
  webhook: 'Webhook',
  system_control: 'System Control',
};

function SettingsChangeChips({ changes }: { changes: SettingsChange[] }) {
  if (!changes || changes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {changes.map((change, i) => (
        <span
          key={i}
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-wt-bg-tertiary border border-wt-border-subtle text-[9px] font-mono text-wt-text-secondary"
        >
          {change.field} = {change.value === null ? 'auto' : String(change.value)}
        </span>
      ))}
    </div>
  );
}

function ActionStepCard({ step }: { step: ActionStep }) {
  const isApproval = step.type === 'approval';
  const isSystemControl = step.action_type === 'system_control';
  const riskColors = step.risk_tier ? RISK_TIER_COLORS[step.risk_tier] : null;
  const Icon = ACTION_TYPE_ICON[step.action_type] ?? Bell;
  const actionLabel = ACTION_TYPE_LABEL[step.action_type] ?? step.action_type;
  const settingsChanges = isSystemControl
    ? (step.action_config?.settings_changes as SettingsChange[] | undefined)
    : undefined;

  return (
    <div
      className={`rounded-md p-2.5 mt-1.5 border ${
        isApproval
          ? 'bg-wt-bg-tertiary border-wt-amber-primary/30 border-l-[3px] border-l-wt-amber-primary'
          : riskColors
          ? `bg-wt-bg-secondary ${riskColors.border} border-l-[3px]`
          : 'bg-wt-bg-secondary border-wt-border-subtle'
      }`}
    >
      {isApproval && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <ShieldAlert className="w-3.5 h-3.5 text-wt-amber-primary" />
          <span className="text-[9px] font-bold uppercase tracking-wider text-wt-amber-primary">
            Requires Approval
          </span>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-wt-text-muted shrink-0" />
        <p className="text-xs font-medium text-wt-text-primary">{step.title}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        <Badge variant="outline" className="text-[9px] h-4 text-wt-text-muted border-wt-border-subtle">
          {actionLabel}
        </Badge>
        {step.subtitle && (
          <span className="text-[10px] text-wt-text-muted">{step.subtitle}</span>
        )}
        {step.risk_tier && riskColors && (
          <Badge variant="outline" className={`text-[9px] h-4 ${riskColors.text} ${riskColors.border}`}>
            {step.risk_tier}
          </Badge>
        )}
      </div>
      {settingsChanges && <SettingsChangeChips changes={settingsChanges} />}
      {isApproval && step.approval_action && (
        <div className="mt-2 px-2 py-1 rounded bg-wt-green-primary/8 border border-wt-green-primary/15 text-[10px] text-wt-green-primary">
          On approve: {step.approval_action}
        </div>
      )}
    </div>
  );
}

interface ActionBlockNodeProps {
  config: ActionBlockConfig;
  stepNum?: number;
  readOnly?: boolean;
  onClick?: () => void;
}

export function ActionBlockNode({ config, stepNum, readOnly, onClick }: ActionBlockNodeProps) {
  return (
    <div
      className="bg-wt-bg-secondary border border-wt-border-subtle rounded-lg overflow-hidden cursor-pointer hover:border-wt-green-primary/40 transition-colors"
      onClick={!readOnly ? onClick : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-wt-border-subtle">
        {stepNum && (
          <span className="w-5 h-5 rounded-full bg-wt-bg-tertiary border border-wt-border-subtle flex items-center justify-center text-[10px] font-semibold font-mono text-wt-text-muted">
            {stepNum}
          </span>
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider text-wt-green-primary">
          THEN
        </span>
      </div>
      <div className="p-3">
        {config.steps.map((step) => (
          <ActionStepCard key={step.id} step={step} />
        ))}
      </div>
    </div>
  );
}
